<template>
  <ul
    v-if="listLength >= 1"
    class="db-list-details-icons"
    :class="{
      'db-list-details-icons-overflow-scroll': overflowScroll === true && dynamicWidth === false,
    }"
    ref="listDetailsIconsWrapper"
  >
    <li
      v-for="(item, index) in listLength"
      :key="item"
      class="db-list-details-icons-item"
      :ref="(el) => (listDetailsIconsItem[index] = el)"
    >
      <DbDetailsIcon v-if="!(dynamicWidth === true && index >= visibleItems && index > 0)">
        <!-- && !(hiddenEmpty === true && $slots.value === undefined) -->
        <template
          v-if="$slots.label"
          v-slot:label
        >
          <slot
            name="label"
            :index="index"
          />
        </template>
        <template
          v-if="$slots.icon"
          v-slot:icon
        >
          <slot
            name="icon"
            :index="index"
          />
        </template>
        <template
          v-if="$slots.tooltiplabel"
          v-slot:tooltiplabel
        >
          <slot
            name="tooltiplabel"
            :index="index"
          />
        </template>
        <template
          v-if="$slots.value"
          v-slot:value
        >
          <slot
            name="value"
            :index="index"
          />
        </template>
        <template
          v-if="$slots.type"
          v-slot:type
        >
          <slot
            name="type"
            :index="index"
          />
        </template>
      </DbDetailsIcon>
    </li>
    <li
      v-if="
        (dynamicWidth && !showAll && listLength > 1 && listLength > visibleItems) ||
        (dynamicWidth && showAll && listLength > 1 && listLength === visibleItems)
      "
      type="button"
      v-on:click="toggleShowAll"
      class="db-list-details-icons-item-show-more"
      ref="listDetailsIconsShowMore"
    >
      <button class="db-list-details-icons-item-show-more-button">
        <IconDbChevron
          width="10"
          height="10"
          class="db-chevron"
          :class="{
            'db-rotate-180': !showAll,
          }"
        />
      </button>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'

const listDetailsIconsWrapper = ref(undefined)
const listDetailsIconsItem = ref<unknown[]>([])
const listDetailsIconsShowMore = ref(undefined)

const showAll = ref(false)
const visibleItems = ref<number>(0)

const props = defineProps<{
  listLength: number
  dynamicWidth?: boolean
  dynamicWidthWithCallBack?: () => {}
  overflowScroll?: boolean
}>()

const computeVisibleItems = () => {
  // If no ListDetailsElement or dynamicWidth is defined, do nothing
  if (!props.dynamicWidth || props.listLength <= 0) {
    return
  }
  // In this case, always put default value
  if (props.listLength <= 1) {
    visibleItems.value = 1
    return
  }
  // If all the ListDetailsElement should be displayed
  if (showAll.value) {
    visibleItems.value = props.listLength
  } else {
    // Or only the first line
    const container = listDetailsIconsWrapper.value
    const containerWidth = container?.offsetWidth || 250 // default size
    const showmore = listDetailsIconsShowMore.value
    const showmoreWidth = showmore?.offsetWidth || 30 // default size
    const gapSpacing = 6
    visibleItems.value = 1
    // Can't compute if one of these failed
    if (!container || !containerWidth || !showmoreWidth) {
      return
    }
    let counterElementToBeDisplayed = 0
    let counterCurrSize = showmoreWidth + gapSpacing // default value is at least one
    for (const itemDetail of listDetailsIconsItem.value) {
      // Get elementDetail width
      let elementDetail = itemDetail as HTMLLIElement
      let elementLabel = 5 // default
      if (
        itemDetail &&
        (itemDetail as HTMLLIElement).querySelector('.db-list-details-icons-item-label')
      ) {
        elementLabel =
          (itemDetail as HTMLLIElement).querySelector('.db-list-details-icons-item-label')
            ?.textContent?.length || 15
      }
      // If elementDetail is not defined
      if (!elementDetail || !elementDetail.offsetWidth) {
        // Estimated this possible size
        counterCurrSize += 56 + Number(7 * elementLabel) + gapSpacing + 2
      } else {
        // Put the elementDetail size
        counterCurrSize += elementDetail.offsetWidth + gapSpacing
      }
      // Check that this is not too long
      if (counterCurrSize >= containerWidth) {
        break
      }
      // Add this new elementDetail
      counterElementToBeDisplayed++
    }
    visibleItems.value = counterElementToBeDisplayed ? counterElementToBeDisplayed : 1 // default value is at least one
  }
  return
}

let resizeObserver = null

function handleResize() {
  computeVisibleItems()
}

onMounted(() => {
  if (props.dynamicWidth && listDetailsIconsWrapper && listDetailsIconsWrapper.value) {
    nextTick(() => {
      computeVisibleItems()
      resizeObserver = new ResizeObserver(handleResize)
      resizeObserver.observe(listDetailsIconsWrapper.value)
    })
  }
})

const toggleShowAll = () => {
  if (props.dynamicWidthWithCallBack) {
    props.dynamicWidthWithCallBack()
  } else {
    showAll.value = !showAll.value
  }
  computeVisibleItems()
}
</script>

<style scoped>
.db-app .db-list-details-icons {
  display: flex;
  flex-flow: wrap;
}

.db-app .db-list-details-icons-overflow-scroll {
  overflow-x: scroll !important;
  flex-wrap: nowrap !important;
  width: 95% !important;
}

.db-app .db-list-details-icons .db-list-details-icons-item {
  justify-self: center;
  display: inline-grid;
  align-items: center;
  grid-auto-flow: column;
  column-gap: var(--db-spacer-4);
  row-gap: 0px;
  min-height: 36px;
  height: min-content;
  border: var(--db_borders_color) solid 0.5px;
  border-radius: 5px;
  padding: 0px 7px 0 7px;
  background-color: var(--db_cards_background_color);
}

.db-app .db-list-details-icons.db-list-details-icons-rows .db-list-details-icons-item {
  justify-self: start;
  grid-auto-flow: row;
  column-gap: 0;
  row-gap: 0;
  grid-template-rows: auto auto;
  grid-template-columns: 35px auto;
}

.db-app .db-list-details-icons .db-list-details-icons-item:empty {
  display: none !important;
}

.db-app .db-details-icon-label {
  font-weight: bold;
}

/** For Amenities a different style is applied */
.db-app .db-card-amenities .db-list-details-icons {
  display: flex;
  flex-wrap: wrap;
  background-color: inherit;
  color: var(--db_box_body_text_color);
  row-gap: 12px;
  column-gap: 25px;
}

.db-app .db-card-amenities .db-list-details-icons .db-list-details-icons-item {
  background-color: inherit;
  height: auto;
  border: none;
  border-radius: 0;
  color: var(--db_box_body_text_color);
  padding: 0;
  padding-right: 6px;
}

.db-app .db-list-details-icons-item-show-more {
  background-color: var(--db_cards_background_color);
  color: var(--db_link_color);
  min-height: 36px;
  border: var(--db_link_color) solid 0.5px;
  border-radius: 5px;
  padding: 0px 8px;
  display: flex;
  cursor: pointer;
}

.db-app .db-room-container-setup-show-more:hover {
  border: var(--db_link_hover_color) solid 0.5px;
  color: var(--db_link_hover_color);
}

.db-app .db-room-container-setup-show-more .db-room-item-setup-show-more {
  display: flex;
  margin: auto;
  cursor: pointer;
}

.db-app .db-list-details-icons-item-show-more .db-list-details-icons-item-show-more-button svg {
  width: 10px;
  height: 10px;
  color: var(--db_link_color);
  fill: var(--db_link_color) !important;
  cursor: pointer;
}
</style>
